import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { t } from '@fetmode/i18n';

export interface OnboardingDockProps {
  selectedCount: number;
  continueLabel: string;
  onContinue: () => void;
  continueDisabled?: boolean;
  onSkip: () => void;
  skipDisabled?: boolean;
}

export function OnboardingDock({
  selectedCount,
  continueLabel,
  onContinue,
  continueDisabled = false,
  onSkip,
  skipDisabled = false,
}: OnboardingDockProps): ReactElement {
  const dockRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const dock = dockRef.current;
    if (!dock) {
      return;
    }

    const sync = (): void => {
      document.documentElement.style.setProperty('--fm-dock-height', `${dock.offsetHeight}px`);
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(dock);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--fm-dock-height');
    };
  }, []);

  return (
    <div ref={dockRef} className="onboarding-dock">
      <p className="onboarding-dock__hint">{t('common.experience.scrollMore')}</p>
      <div className="onboarding-dock__row">
        <p className="onboarding-dock__count">
          {t('common.experience.selectedCount').replace('{{count}}', String(selectedCount))}
        </p>
        <div className="onboarding-dock__buttons">
          <button
            type="button"
            className="onboarding-dock__skip"
            disabled={skipDisabled}
            onClick={onSkip}
          >
            {t('common.experience.dockSkip')}
          </button>
          <button
            type="button"
            className="onboarding-dock__continue"
            disabled={continueDisabled}
            onClick={onContinue}
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
